/**
 * Behind-the-ball perspective renderer for the Curvle pitch.
 *
 * Pure 2D canvas — no Three.js, no WebGL — to keep the bundle tiny and the
 * RAF cost negligible on phones. We project world-space (x right, y up,
 * z toward goal) to screen-space through one virtual camera positioned
 * 3 m behind the ball at eye height.
 *
 * Drawing order is back-to-front: sky → ground → far yard lines → goal/net
 * → keeper → wall → trajectory → ball → aim arrow.
 *
 * Step 4.5: takes PublicPuzzleParams (no keeper secrets). The keeper is
 * drawn at restPos by default; once the server reveals diveTo + timing in
 * the SceneInputs.keeperReveal block, the renderer animates the dive.
 */
import { keeperPosAt } from "./engine";
import type {
  AimInput,
  KeeperParams,
  KeeperReveal,
  PublicPuzzleParams,
  Vec3,
} from "./types";
import type { CanvasView } from "./canvas";
import { drawWallPlayer } from "./sprites/wallSprite";
import { drawKeeperSprite } from "./sprites/keeperSprite";

interface ScreenPoint { x: number; y: number; depth: number; visible: boolean }

/**
 * Vertical anchor of the camera's principal point (the point on screen
 * where world y=camera.y projects to). Default would be 0.5 (geometric
 * centre of canvas). We use 0.4 to shift the entire scene UP on screen —
 * the ball at (0,0,0) and the goal at z=18 then sit higher in the canvas,
 * leaving less sky and more visible field. Without this shift, the ball
 * ends up near the bottom of the viewport on phones and short laptop
 * screens, requiring scrolling to drag-aim.
 *
 * 0.5 = traditional centred horizon, lots of sky
 * 0.4 = horizon 40% from top, more field, ball roughly 78% down
 * 0.32 = aggressive shift, ball ~70% down but the goal feels too close
 */
const CAMERA_PRINCIPAL_Y = 0.4;

class Camera {
  constructor(public pos: Vec3, public focal: number) {}
  project(p: Vec3, W: number, H: number): ScreenPoint {
    const dz = p.z - this.pos.z;
    if (dz <= 0.1) return { x: 0, y: 0, depth: dz, visible: false };
    const dx = p.x - this.pos.x;
    const dy = p.y - this.pos.y;
    return {
      x: (dx * this.focal) / dz + W / 2,
      y: H * CAMERA_PRINCIPAL_Y - (dy * this.focal) / dz,
      depth: dz,
      visible: true,
    };
  }
}

export interface SceneInputs {
  ball: Vec3;
  replayTime: number;
  phase: "LOADING" | "AIMING" | "REPLAYING" | "RESULT";
  aim: AimInput | null;
  preview: Vec3[] | null;
  /** Server-revealed keeper dive params; null until /api/attempt response lands. */
  keeperReveal: KeeperReveal | null;
}

export class PitchRenderer {
  private camera: Camera;
  /** Last screen position of the ball — used to compute spin rotation
   *  so the Telstar pattern visually "rolls" in the direction of travel. */
  private lastBallScreen: { x: number; y: number } | null = null;
  /** Accumulated rotation in radians; advances each frame based on ball
   *  velocity. Stays stable when the ball is at rest in AIMING. */
  private ballRotation = 0;
  /** Last computed keeper diagnostic snapshot. Updated each draw call when
   *  debug=1 is set so the HUD layer can render it. */
  private lastKeeperDebug: {
    posX: number; posY: number;
    feetX: number; feetY: number;
    leadX: number; leadY: number;
    dive: number;
    reveal: boolean;
  } | null = null;
  /** Sticky snapshot of the LAST keeperReveal that was ever observed by the
   *  renderer in this session. Survives phase transitions and game resets so
   *  one screenshot taken after the kick reveals what happened during the
   *  replay window. Only populated when debug=1. */
  private stickyReveal: {
    diveToX: number; diveToY: number;
    restX: number; restY: number;
    reactionTime: number;
    diveDuration: number;
    reach: number;
    seenAtMs: number;
  } | null = null;
  /** Maximum dive ever observed across this session. Tells us whether the
   *  dive animation actually ran past the smoothstep threshold. */
  private stickyMaxDive = 0;
  /** Maximum |pos.x - restPos.x| ever observed in world coords. */
  private stickyMaxLatMove = 0;
  /** Maximum lead-glove screen-space distance from rest projection. */
  private stickyMaxScreenMove = 0;
  /** restPos lead-glove screen position, measured once when keeper is at
   *  rest, so we can compare runtime positions against it. */
  private restLeadScreen: { x: number; y: number } | null = null;
  /** Toggle on with `?debug=1` in the URL. Draws an on-canvas HUD with the
   *  values the keeper sprite was just computed from — lets us see in one
   *  screenshot whether the dive data is reaching the renderer. */
  private debugHud = false;

  constructor(private view: CanvasView, private params: PublicPuzzleParams) {
    const focal = Math.min(view.cssWidth, view.cssHeight) * 0.85;
    this.camera = new Camera({ x: 0, y: 1.5, z: -3 }, focal);
    if (typeof window !== "undefined") {
      try {
        this.debugHud = new URLSearchParams(window.location.search).get("debug") === "1";
      } catch {
        /* noop */
      }
    }
  }

  resync(): void {
    this.camera.focal = Math.min(this.view.cssWidth, this.view.cssHeight) * 0.85;
  }

  draw(scene: SceneInputs): void {
    const { ctx, cssWidth: W, cssHeight: H } = this.view;
    ctx.clearRect(0, 0, W, H);

    this.drawSky(W, H);
    this.drawGround(W, H);
    this.drawYardLines(W, H);
    this.drawGoal(W, H);
    // Painters' algorithm says wall (z=9.15) on top of keeper (z=18). We
    // intentionally invert that — the keeper is the *action* during replay,
    // so we want their arms/gloves visible above the wall as they dive.
    this.drawWall(W, H);
    this.drawKeeper(scene.replayTime, scene.keeperReveal, W, H);
    if (scene.preview && scene.phase === "AIMING") this.drawTrajectory(scene.preview, W, H);
    this.drawBall(scene.ball, W, H);
    if (scene.phase === "AIMING" && scene.aim) this.drawAimArrow(scene.aim, W, H);
    if (this.debugHud) this.drawDebugHud(scene, scene.replayTime, W, H);
  }

  /**
   * Diagnostic overlay — drawn last so it sits on top of everything. Only
   * active when the URL has `?debug=1`. Shows the exact values the keeper
   * was just rendered from so we can see at a glance whether reveal data
   * is reaching the canvas.
   */
  private drawDebugHud(scene: SceneInputs, keeperT: number, _W: number, _H: number): void {
    const ctx = this.view.ctx;
    const r = scene.keeperReveal;
    const k = this.lastKeeperDebug;
    const lines: string[] = [
      `phase=${scene.phase}`,
      `replayT=${scene.replayTime.toFixed(3)}  keeperT=${keeperT.toFixed(3)}`,
      `reveal=${r ? "yes" : "NO"}`,
    ];
    if (r) {
      lines.push(
        `  diveTo=(${r.diveTo.x.toFixed(2)},${r.diveTo.y.toFixed(2)})`,
        `  rest=(${this.params.keeper.restPos.x.toFixed(2)},${this.params.keeper.restPos.y.toFixed(2)})`,
        `  react=${r.reactionTime.toFixed(2)}s  dur=${r.diveDuration.toFixed(2)}s  reach=${r.reach.toFixed(2)}`,
      );
    }
    if (k) {
      lines.push(
        `dive=${k.dive.toFixed(3)}`,
        `pos=(${k.posX.toFixed(2)},${k.posY.toFixed(2)})  world`,
        `feet=(${k.feetX.toFixed(0)},${k.feetY.toFixed(0)})  screen`,
        `lead=(${k.leadX.toFixed(0)},${k.leadY.toFixed(0)})  screen`,
      );
    }

    // Sticky section — survives phase resets so a screenshot taken AFTER
    // the kick still shows what landed during the replay window.
    lines.push("--- session sticky ---");
    if (this.stickyReveal) {
      const sr = this.stickyReveal;
      lines.push(
        `revealEverSeen=YES  age=${((performance.now() - sr.seenAtMs) / 1000).toFixed(1)}s`,
        `  diveTo=(${sr.diveToX.toFixed(2)},${sr.diveToY.toFixed(2)})  rest=(${sr.restX.toFixed(2)},${sr.restY.toFixed(2)})`,
        `  react=${sr.reactionTime.toFixed(2)}s  dur=${sr.diveDuration.toFixed(2)}s  reach=${sr.reach.toFixed(2)}`,
      );
    } else {
      lines.push(`revealEverSeen=NO`);
    }
    lines.push(
      `maxDive=${this.stickyMaxDive.toFixed(3)} (need >0 to confirm dive ran)`,
      `maxLatMove=${this.stickyMaxLatMove.toFixed(2)}m world`,
      `maxScreenMove=${this.stickyMaxScreenMove.toFixed(0)}px screen`,
    );

    ctx.save();
    ctx.fillStyle = "rgba(0,0,0,0.78)";
    ctx.fillRect(8, 8, 320, lines.length * 16 + 12);
    ctx.fillStyle = "#7ee7c5";
    ctx.font = "12px ui-monospace, Menlo, Consolas, monospace";
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    for (let i = 0; i < lines.length; i++) {
      ctx.fillText(lines[i]!, 14, 14 + i * 16);
    }
    ctx.restore();
  }

  // ---- background ----
  private drawSky(W: number, H: number): void {
    const ctx = this.view.ctx;
    // Match the camera's principal-point shift so sky/horizon/ground line
    // up at the same y. Both this and drawGround must agree or the sky
    // band leaves a gap or overlap with the field.
    const horizonY = H * CAMERA_PRINCIPAL_Y;
    const g = ctx.createLinearGradient(0, 0, 0, horizonY);
    g.addColorStop(0, "#0a253a");
    g.addColorStop(1, "#163e57");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, horizonY);

    // Stadium roofline + crowd silhouette band running just above the horizon.
    // Pure decoration — no projection, just a screen-space band that looks
    // like a packed stadium tier viewed at distance. Dark on top, lighter
    // toward the horizon where the lower tier "lights" hit.
    const crowdBandH = Math.max(28, H * 0.06);
    const crowdTopY = horizonY - crowdBandH;
    const crowdG = ctx.createLinearGradient(0, crowdTopY, 0, horizonY);
    crowdG.addColorStop(0, "#02101a");
    crowdG.addColorStop(0.65, "#06202e");
    crowdG.addColorStop(1, "#0e3a4f");
    ctx.fillStyle = crowdG;
    ctx.fillRect(0, crowdTopY, W, crowdBandH);

    // Stadium roof — slight dome arcs cutting into the top of the crowd
    // band so the silhouette doesn't read as a flat rectangle.
    ctx.fillStyle = "#020a12";
    const archCount = 14;
    const archW = W / archCount;
    const archDepth = crowdBandH * 0.55;
    for (let i = 0; i <= archCount; i++) {
      const cx = i * archW;
      ctx.beginPath();
      ctx.ellipse(cx, crowdTopY, archW * 0.55, archDepth, 0, 0, Math.PI, true);
      ctx.fill();
    }

    // Scatter of bright pixel-flicker "phone lights" across the crowd band
    // for atmosphere. Deterministic positions so they don't strobe per
    // frame; seeded from a simple hash of (i, w) so they redistribute
    // sensibly across screen widths.
    ctx.fillStyle = "rgba(255, 220, 140, 0.55)";
    for (let i = 0; i < 90; i++) {
      const sx = ((i * 9301 + 49297) % 233280) / 233280; // 0..1
      const sy = ((i * 6151 + 17321) % 233280) / 233280;
      const x = sx * W;
      const y = crowdTopY + crowdBandH * 0.35 + sy * crowdBandH * 0.55;
      ctx.fillRect(x, y, 1.4, 1.4);
    }
  }

  private drawGround(W: number, H: number): void {
    const ctx = this.view.ctx;
    // Solid base — fills any cracks between stripes near the horizon.
    const horizonY = this.camera.project({ x: 0, y: 0, z: 1000 }, W, H).y;
    const base = ctx.createLinearGradient(0, horizonY, 0, H);
    base.addColorStop(0, "#0a5a26");
    base.addColorStop(1, "#073e1a");
    ctx.fillStyle = base;
    ctx.fillRect(0, horizonY, W, H - horizonY);

    // Mowed-grass stripes running across the field. Alternate two greens
    // every 4 m of depth. Perspective projection of the four corners ensures
    // they taper correctly as they recede.
    const halfWidth = 50; // a bit wider than the visible pitch
    const stripeDepth = 4;
    const zNear = -5;
    const zFar = 70;
    for (let z = zNear; z < zFar; z += stripeDepth) {
      const dark = Math.floor((z + 100) / stripeDepth) % 2 === 0;
      ctx.fillStyle = dark ? "#0e7333" : "#106f30";
      const bl = this.camera.project({ x: -halfWidth, y: 0, z }, W, H);
      const br = this.camera.project({ x: halfWidth, y: 0, z }, W, H);
      const tr = this.camera.project({ x: halfWidth, y: 0, z: z + stripeDepth }, W, H);
      const tl = this.camera.project({ x: -halfWidth, y: 0, z: z + stripeDepth }, W, H);
      if (!bl.visible || !br.visible || !tr.visible || !tl.visible) continue;
      ctx.beginPath();
      ctx.moveTo(bl.x, bl.y);
      ctx.lineTo(br.x, br.y);
      ctx.lineTo(tr.x, tr.y);
      ctx.lineTo(tl.x, tl.y);
      ctx.closePath();
      ctx.fill();
    }
  }

  private drawWorldLine(a: Vec3, b: Vec3, W: number, H: number): void {
    const pa = this.camera.project(a, W, H);
    const pb = this.camera.project(b, W, H);
    if (!pa.visible || !pb.visible) return;
    const ctx = this.view.ctx;
    ctx.beginPath();
    ctx.moveTo(pa.x, pa.y);
    ctx.lineTo(pb.x, pb.y);
    ctx.stroke();
  }

  private drawYardLines(W: number, H: number): void {
    const ctx = this.view.ctx;
    const goalZ = this.params.goal.distance; // = 18
    // Standard FIFA dimensions
    const PEN_AREA_WIDTH = 40.32; // half = 20.16
    const PEN_AREA_DEPTH = 16.5;
    const SIX_YARD_WIDTH = 18.32; // half = 9.16
    const SIX_YARD_DEPTH = 5.5;
    const PEN_SPOT_DIST = 11; // metres from goal line
    const halfPen = PEN_AREA_WIDTH / 2;
    const halfSix = SIX_YARD_WIDTH / 2;

    ctx.strokeStyle = "rgba(255,255,255,0.85)";
    ctx.lineWidth = 2;

    // Goal line — extend beyond goal posts to the touchline.
    this.drawWorldLine({ x: -halfPen - 5, y: 0, z: goalZ }, { x: halfPen + 5, y: 0, z: goalZ }, W, H);

    // Penalty area: front edge + two side edges.
    const penFrontZ = goalZ - PEN_AREA_DEPTH;
    this.drawWorldLine({ x: -halfPen, y: 0, z: penFrontZ }, { x: halfPen, y: 0, z: penFrontZ }, W, H);
    this.drawWorldLine({ x: -halfPen, y: 0, z: penFrontZ }, { x: -halfPen, y: 0, z: goalZ }, W, H);
    this.drawWorldLine({ x: halfPen, y: 0, z: penFrontZ }, { x: halfPen, y: 0, z: goalZ }, W, H);

    // Six-yard box.
    const sixFrontZ = goalZ - SIX_YARD_DEPTH;
    this.drawWorldLine({ x: -halfSix, y: 0, z: sixFrontZ }, { x: halfSix, y: 0, z: sixFrontZ }, W, H);
    this.drawWorldLine({ x: -halfSix, y: 0, z: sixFrontZ }, { x: -halfSix, y: 0, z: goalZ }, W, H);
    this.drawWorldLine({ x: halfSix, y: 0, z: sixFrontZ }, { x: halfSix, y: 0, z: goalZ }, W, H);

    // Penalty spot.
    const penSpotZ = goalZ - PEN_SPOT_DIST;
    const spot = this.camera.project({ x: 0, y: 0, z: penSpotZ }, W, H);
    if (spot.visible) {
      const r = Math.max(2, 100 / Math.max(1, spot.depth * 1.2));
      ctx.fillStyle = "rgba(255,255,255,0.85)";
      ctx.beginPath();
      ctx.arc(spot.x, spot.y, r, 0, Math.PI * 2);
      ctx.fill();
    }

    // Faint kick-position marker right where the ball sits.
    ctx.strokeStyle = "rgba(255,255,255,0.18)";
    ctx.lineWidth = 1;
    this.drawWorldLine({ x: -2, y: 0, z: 0 }, { x: 2, y: 0, z: 0 }, W, H);
  }

  private drawGoal(W: number, H: number): void {
    const p = this.params.goal;
    const z = p.distance;
    const halfW = p.width / 2;
    const ctx = this.view.ctx;

    ctx.fillStyle = "rgba(255,255,255,0.07)";
    const tl = this.camera.project({ x: -halfW, y: p.height, z: z + 1.5 }, W, H);
    const tr = this.camera.project({ x: halfW, y: p.height, z: z + 1.5 }, W, H);
    const br = this.camera.project({ x: halfW, y: 0, z: z + 1.5 }, W, H);
    const bl = this.camera.project({ x: -halfW, y: 0, z: z + 1.5 }, W, H);
    if (tl.visible && tr.visible && br.visible && bl.visible) {
      ctx.beginPath();
      ctx.moveTo(tl.x, tl.y);
      ctx.lineTo(tr.x, tr.y);
      ctx.lineTo(br.x, br.y);
      ctx.lineTo(bl.x, bl.y);
      ctx.closePath();
      ctx.fill();
    }

    // Real goal-net diamond mesh. Replaces the previous rectangular grid.
    // We draw two sets of diagonal lines (rising and falling) that together
    // tile the goal area with diamonds — the classic goal net look.
    // Density: 12 columns × 6 rows yields ~72 diamonds, matching the cadence
    // of a real net at this distance without becoming visual noise.
    ctx.strokeStyle = "rgba(255,255,255,0.22)";
    ctx.lineWidth = 1;
    const COLS = 12;
    const ROWS = 6;
    const colStep = p.width / COLS;
    const rowStep = p.height / ROWS;
    // Rising diagonals: bottom-left → top-right.
    for (let k = -ROWS; k <= COLS; k++) {
      const x0 = -halfW + k * colStep;
      const y0 = 0;
      const x1 = x0 + ROWS * colStep;
      const y1 = ROWS * rowStep;
      // Clip to the goal frame in world space.
      const clip = (x: number, y: number): { x: number; y: number } => ({
        x: Math.max(-halfW, Math.min(halfW, x)),
        y: Math.max(0, Math.min(p.height, y)),
      });
      const c0 = clip(x0, y0);
      const c1 = clip(x1, y1);
      if (c0.x === c1.x && c0.y === c1.y) continue;
      this.drawWorldLine({ x: c0.x, y: c0.y, z: z + 1.5 }, { x: c1.x, y: c1.y, z: z + 1.5 }, W, H);
    }
    // Falling diagonals: top-left → bottom-right.
    for (let k = 0; k <= COLS + ROWS; k++) {
      const x0 = -halfW + k * colStep;
      const y0 = p.height;
      const x1 = x0 - ROWS * colStep;
      const y1 = 0;
      const clip = (x: number, y: number): { x: number; y: number } => ({
        x: Math.max(-halfW, Math.min(halfW, x)),
        y: Math.max(0, Math.min(p.height, y)),
      });
      const c0 = clip(x0, y0);
      const c1 = clip(x1, y1);
      if (c0.x === c1.x && c0.y === c1.y) continue;
      this.drawWorldLine({ x: c0.x, y: c0.y, z: z + 1.5 }, { x: c1.x, y: c1.y, z: z + 1.5 }, W, H);
    }

    ctx.strokeStyle = "rgba(255,255,255,0.95)";
    ctx.lineWidth = 3;
    this.drawWorldLine({ x: -halfW, y: 0, z }, { x: -halfW, y: p.height, z }, W, H);
    this.drawWorldLine({ x: halfW, y: 0, z }, { x: halfW, y: p.height, z }, W, H);
    this.drawWorldLine({ x: -halfW, y: p.height, z }, { x: halfW, y: p.height, z }, W, H);
  }

  // Art pass v2: per-player sprite rendering lives in ./sprites/wallSprite.ts.
  // This function still owns world-space → screen-space projection so the
  // physics anchor points remain authoritative.
  private drawWall(W: number, H: number): void {
    const w = this.params.wall;
    const ctx = this.view.ctx;

    const PLAYER_WIDTH = 0.5;
    const headRadiusM = w.height * 0.07;
    const shoulderM = w.height - headRadiusM * 2;
    const halfWall = (w.players * PLAYER_WIDTH) / 2;

    for (let i = 0; i < w.players; i++) {
      const cx = -halfWall + PLAYER_WIDTH * (i + 0.5);
      const feet = this.camera.project({ x: cx, y: 0, z: w.distance }, W, H);
      const shoulder = this.camera.project({ x: cx, y: shoulderM, z: w.distance }, W, H);
      const head = this.camera.project({ x: cx, y: shoulderM + headRadiusM, z: w.distance }, W, H);
      if (!feet.visible || !shoulder.visible || !head.visible) continue;

      const torsoPx = (PLAYER_WIDTH * 0.85 * this.camera.focal) / w.distance;
      const headPx = (headRadiusM * this.camera.focal) / w.distance;

      drawWallPlayer(ctx, { feet, shoulder, head, torsoPx, headPx, index: i });
    }
  }

  /**
   * Goalkeeper sprite — full-height humanoid that DIVES.
   *
   * `pos` from keeperPosAt is the position of the LEAD GLOVE (catch centre)
   * — that's what the physics tests against the ball. So the world model is:
   *   feet  — anchored on ground; slides a fraction of the lateral dive
   *   torso — leans toward the gloves as `dive` (0..1) progresses
   *   head  — sits just beyond the torso top in the lean direction
   *   gloves — at world (pos.x, pos.y), i.e. moves from chest height (idle)
   *            to whatever low/high corner the keeper is reaching for
   */
  private drawKeeper(replayTime: number, reveal: KeeperReveal | null, W: number, H: number): void {
    const restPos = this.params.keeper.restPos;
    let pos: { x: number; y: number };
    let dive = 0;       // 0 = idle, 1 = peak dive

    if (reveal) {
      const k: KeeperParams = {
        restPos,
        diveTo: reveal.diveTo,
        reactionTime: reveal.reactionTime,
        diveDuration: reveal.diveDuration,
        reach: reveal.reach,
      };
      pos = keeperPosAt(replayTime, k);

      if (replayTime <= reveal.reactionTime) {
        dive = 0;
      } else if (replayTime >= reveal.reactionTime + reveal.diveDuration) {
        dive = 1;
      } else {
        const u = (replayTime - reveal.reactionTime) / reveal.diveDuration;
        dive = u * u * (3 - 2 * u); // smoothstep
      }
    } else {
      pos = restPos;
    }

    const ctx = this.view.ctx;
    const z = this.params.goal.distance - 0.3;

    // World keypoints. All y in metres above ground, x in metres lateral.
    const TORSO_LEN = 0.95;
    const HIP_HEIGHT_IDLE = 0.95;
    const SHOULDER_HEIGHT_IDLE = HIP_HEIGHT_IDLE + TORSO_LEN; // ~1.9m

    // Feet slide a bit toward the dive — keepers push off.
    const feetX = restPos.x + (pos.x - restPos.x) * 0.45 * dive;
    const feetY = 0.05 * dive; // slight lift mid-dive
    // Shoulder lerps from "directly above feet" toward "next to gloves".
    // Direction toward gloves in world space.
    const gx = pos.x;
    const gy = pos.y;
    // Hip — pelvis position; trends toward gloves but stays lower & nearer feet.
    const hipX = restPos.x + (pos.x - restPos.x) * 0.6 * dive;
    const hipY = HIP_HEIGHT_IDLE * (1 - 0.55 * dive) + gy * 0.55 * dive;
    // Shoulder — torso top; near the gloves at peak dive.
    const shoX = restPos.x + (pos.x - restPos.x) * 0.85 * dive;
    const shoY = SHOULDER_HEIGHT_IDLE * (1 - 0.65 * dive) + gy * 0.65 * dive;
    // Head — slightly past shoulder in body-axis direction.
    const headOffX = (shoX - hipX) * 0.25;
    const headOffY = (shoY - hipY) * 0.25;
    const headX = shoX + headOffX;
    const headY = shoY + headOffY + 0.18 * (1 - dive); // taller stance when idle
    // Trail glove — opposite side / behind body.
    const trailX = restPos.x + (restPos.x - pos.x) * 0.4 * dive;
    const trailY = HIP_HEIGHT_IDLE + 0.2 * (1 - dive) - 0.1 * dive;

    const feet = this.camera.project({ x: feetX, y: feetY, z }, W, H);
    const hip = this.camera.project({ x: hipX, y: hipY, z }, W, H);
    const sho = this.camera.project({ x: shoX, y: shoY, z }, W, H);
    const head = this.camera.project({ x: headX, y: headY, z }, W, H);
    const lead = this.camera.project({ x: gx, y: gy, z }, W, H);
    const trail = this.camera.project({ x: trailX, y: trailY, z }, W, H);
    // Snapshot for the optional debug HUD — captured BEFORE any visibility
    // bail-out so the HUD reflects the truth even if projection failed.
    this.lastKeeperDebug = {
      posX: pos.x,
      posY: pos.y,
      feetX: feet.x,
      feetY: feet.y,
      leadX: lead.x,
      leadY: lead.y,
      dive,
      reveal: reveal !== null,
    };
    // Maintain sticky diagnostics so we can inspect them after the replay
    // is over. Reveal is captured the FIRST frame it appears.
    if (this.debugHud) {
      if (reveal && !this.stickyReveal) {
        this.stickyReveal = {
          diveToX: reveal.diveTo.x,
          diveToY: reveal.diveTo.y,
          restX: restPos.x,
          restY: restPos.y,
          reactionTime: reveal.reactionTime,
          diveDuration: reveal.diveDuration,
          reach: reveal.reach,
          seenAtMs: typeof performance !== "undefined" ? performance.now() : 0,
        };
      }
      if (!this.restLeadScreen) {
        const restLead = this.camera.project({ x: restPos.x, y: restPos.y, z }, W, H);
        if (restLead.visible) this.restLeadScreen = { x: restLead.x, y: restLead.y };
      }
      if (dive > this.stickyMaxDive) this.stickyMaxDive = dive;
      const latMove = Math.abs(pos.x - restPos.x);
      if (latMove > this.stickyMaxLatMove) this.stickyMaxLatMove = latMove;
      if (this.restLeadScreen) {
        const sdx = lead.x - this.restLeadScreen.x;
        const sdy = lead.y - this.restLeadScreen.y;
        const sd = Math.sqrt(sdx * sdx + sdy * sdy);
        if (sd > this.stickyMaxScreenMove) this.stickyMaxScreenMove = sd;
      }
    }

    if (!feet.visible || !hip.visible || !sho.visible || !head.visible || !lead.visible || !trail.visible) return;

    const bodyPx = Math.max(8, (0.55 * this.camera.focal) / z);

    // Shadow projection (kept here because it uses restPos + dive, which
    // are physics-state inputs that the sprite file shouldn't know about).
    const shadowProj = this.camera.project({ x: restPos.x + (pos.x - restPos.x) * 0.3 * dive, y: 0, z }, W, H);
    const restLead = this.camera.project({ x: restPos.x, y: restPos.y, z }, W, H);

    drawKeeperSprite(ctx, {
      feet,
      hip,
      shoulder: sho,
      head,
      lead,
      trail,
      restLead,
      bodyPx,
      dive,
      shadow: shadowProj,
    });
  }

  private drawTrajectory(traj: Vec3[], W: number, H: number): void {
    const ctx = this.view.ctx;

    // Project every sample once so we can render a smooth tapered line
    // instead of the dotted-arc style. Tapered: thick near the ball, thin
    // at the goal — gives a stronger sense of motion and depth.
    const pts: Array<{ x: number; y: number; depth: number }> = [];
    for (let i = 0; i < traj.length; i++) {
      const p = this.camera.project(traj[i]!, W, H);
      if (p.visible) pts.push({ x: p.x, y: p.y, depth: p.depth });
    }
    if (pts.length < 2) return;

    // Outline (soft halo) then bright core, so the line reads on any background.
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "rgba(255,255,255,0.18)";
    for (let i = 1; i < pts.length; i++) {
      const a = pts[i - 1]!;
      const b = pts[i]!;
      const w = Math.max(2, 14 / Math.max(1, ((a.depth + b.depth) / 2) * 0.45));
      ctx.lineWidth = w;
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
    }
    ctx.strokeStyle = "rgba(255,255,255,0.9)";
    for (let i = 1; i < pts.length; i++) {
      const a = pts[i - 1]!;
      const b = pts[i]!;
      const w = Math.max(1, 6 / Math.max(1, ((a.depth + b.depth) / 2) * 0.45));
      ctx.lineWidth = w;
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
    }
  }

  private drawBall(world: Vec3, W: number, H: number): void {
    const p = this.camera.project(world, W, H);
    if (!p.visible) {
      this.lastBallScreen = null;
      return;
    }
    const r = Math.max(3, 36 / Math.max(1, p.depth * 0.45));
    const ctx = this.view.ctx;

    // Drop shadow on the ground (project ball onto y=0 plane).
    const shadow = this.camera.project({ x: world.x, y: 0, z: world.z }, W, H);
    if (shadow.visible) {
      ctx.fillStyle = "rgba(0,0,0,0.35)";
      ctx.beginPath();
      ctx.ellipse(shadow.x, shadow.y, r * 0.9, r * 0.3, 0, 0, Math.PI * 2);
      ctx.fill();
    }

    // Update rotation from screen-space velocity. The ball appears to roll in
    // the direction of motion at ~1 full turn per ball-circumference travelled.
    if (this.lastBallScreen) {
      const dx = p.x - this.lastBallScreen.x;
      const dy = p.y - this.lastBallScreen.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      // 1 rotation = 2*PI radians for traversing 2*PI*r screen pixels.
      this.ballRotation += dist / Math.max(1, r);
    }
    this.lastBallScreen = { x: p.x, y: p.y };

    drawTelstarBall(ctx, p.x, p.y, r, this.ballRotation);
  }

  private drawAimArrow(aim: AimInput, W: number, H: number): void {
    const ctx = this.view.ctx;
    const ball = this.camera.project({ x: this.params.ball.x, y: this.params.ball.y, z: 0 }, W, H);
    if (!ball.visible) return;
    const len = 30 + 120 * aim.power;
    const angleRad = aim.angle * (Math.PI / 4);
    const tipX = ball.x + Math.sin(angleRad) * len;
    const tipY = ball.y - Math.cos(angleRad) * len;
    ctx.strokeStyle = `rgba(255,255,255,${0.3 + 0.6 * aim.power})`;
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(ball.x, ball.y);
    ctx.lineTo(tipX, tipY);
    ctx.stroke();
    const headSize = 10;
    ctx.fillStyle = ctx.strokeStyle;
    ctx.beginPath();
    ctx.moveTo(tipX, tipY);
    ctx.lineTo(tipX - Math.sin(angleRad - 0.5) * headSize, tipY + Math.cos(angleRad - 0.5) * headSize);
    ctx.lineTo(tipX - Math.sin(angleRad + 0.5) * headSize, tipY + Math.cos(angleRad + 0.5) * headSize);
    ctx.closePath();
    ctx.fill();
    if (Math.abs(aim.curl) > 0.05) {
      ctx.fillStyle = "rgba(255,255,255,0.85)";
      ctx.font = "bold 14px ui-sans-serif";
      ctx.textAlign = "center";
      const sign = aim.curl > 0 ? "↻" : "↺";
      ctx.fillText(sign, tipX + 18 * Math.sign(aim.curl), tipY);
    }
  }
}

/**
 * Draw a stylised Telstar football — the classic 32-panel pattern (one
 * black pentagon in the centre with five black hexagons around it on the
 * visible front of the sphere). Procedural, no image asset required.
 */
function drawTelstarBall(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  radius: number,
  rotation: number,
): void {
  ctx.save();

  const base = ctx.createRadialGradient(
    cx - radius * 0.35,
    cy - radius * 0.35,
    radius * 0.05,
    cx,
    cy,
    radius * 1.05,
  );
  base.addColorStop(0, "#ffffff");
  base.addColorStop(0.55, "#f0f3f6");
  base.addColorStop(1, "#9ca3af");
  ctx.fillStyle = base;
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.fill();

  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.clip();

  ctx.translate(cx, cy);
  ctx.rotate(rotation);

  ctx.fillStyle = "#0f172a";
  drawPolygon(ctx, 0, 0, radius * 0.32, 5, -Math.PI / 2);

  for (let i = 0; i < 5; i++) {
    const a = -Math.PI / 2 + (i / 5) * Math.PI * 2;
    const dist = radius * 0.62;
    const px = Math.cos(a) * dist;
    const py = Math.sin(a) * dist;
    drawPolygon(ctx, px, py, radius * 0.16, 6, a + Math.PI / 6);
  }

  ctx.restore();

  const hl = ctx.createRadialGradient(
    cx - radius * 0.4,
    cy - radius * 0.4,
    0,
    cx - radius * 0.4,
    cy - radius * 0.4,
    radius * 0.55,
  );
  hl.addColorStop(0, "rgba(255,255,255,0.55)");
  hl.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = hl;
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.fill();

  ctx.strokeStyle = "rgba(15,23,42,0.5)";
  ctx.lineWidth = Math.max(1, radius * 0.05);
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.stroke();

  ctx.restore();
}

function drawPolygon(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  sides: number,
  rotation: number,
): void {
  ctx.beginPath();
  for (let i = 0; i < sides; i++) {
    const a = rotation + (i / sides) * Math.PI * 2;
    const x = cx + Math.cos(a) * r;
    const y = cy + Math.sin(a) * r;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
  ctx.fill();
}
